import React, { useEffect, useMemo, useState } from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
  SafeAreaView,
  StatusBar,
  ScrollView,
  ActivityIndicator,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { DualPaneLayout, colors, spacing, typography } from '../components';
import { DirectionalIcon } from '../components/DirectionalIcon';
import { useAppContext } from '../contexts/AppContext';
import { useAppStore } from '../store/appStore';
import { apiService, BillingPlan, BillingOverview, BillingCycle, BillingCatalog } from '../services/api';
import { activateBusinessSession } from '../services/business-session';
import { startCheckout, canPayInApp } from '../services/checkout';
import { handleApiError } from '../utils/errorHandler';
import { useLocale } from '../contexts/LocaleContext';
import { useTypography } from '../hooks/useTypography';
import { useTheme } from '../contexts/ThemeContext';
import { useThemedStyles } from '../hooks/useThemedStyles';
import { useHardwareBackAction } from '../navigation/hardwareBack';
import { withAlpha } from '../theme';

const CYCLES: BillingCycle[] = ['monthly', 'quarterly', 'yearly'];
const CYCLE_MONTHS: Record<BillingCycle, number> = { monthly: 1, quarterly: 3, yearly: 12 };

const priceFor = (plan: BillingPlan, cycle: BillingCycle) =>
  cycle === 'yearly' ? plan.yearPrice : cycle === 'quarterly' ? plan.quartPrice : plan.moPrice;

const formatMoney = (amount: number, currency: string, locale: string) => {
  try {
    return new Intl.NumberFormat(locale === 'ar' ? 'ar-EG' : 'en-US', { style: 'currency', currency, maximumFractionDigits: amount % 1 ? 2 : 0 }).format(amount);
  } catch {
    return `${currency} ${amount}`;
  }
};

/**
 * Choose a plan — onboarding (after creating a business) and "Change plan"
 * from Billing (routeParams.mode === 'change'). The server decides what a
 * choice means: free plans and first-time trials activate immediately,
 * anything else opens Kashier checkout.
 */
export const PlanSelectionScreen: React.FC = () => {
  const styles = useThemedStyles(getStyles);
  const { showNotification, navigateTo, routeParams } = useAppContext();
  const { currentBusiness } = useAppStore();
  const { t, isRTL, locale } = useLocale();
  const typo = useTypography();
  const { themeMode } = useTheme();
  const isChangeMode = (routeParams as any)?.mode === 'change';

  const [plans, setPlans] = useState<BillingPlan[]>([]);
  const [overview, setOverview] = useState<BillingOverview | null>(null);
  const [catalog, setCatalog] = useState<BillingCatalog | null>(null);
  const [cycle, setCycle] = useState<BillingCycle>('monthly');
  const [isLoading, setIsLoading] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);
  const [busyPlanId, setBusyPlanId] = useState<string | null>(null);

  const goBack = isChangeMode ? () => navigateTo('subscription') : undefined;
  useHardwareBackAction(goBack);

  const load = async () => {
    setIsLoading(true);
    setLoadFailed(false);
    try {
      const [plansRes, overviewRes, catalogRes] = await Promise.all([
        apiService.getBillingPlans(),
        apiService.getBillingOverview(),
        apiService.getBillingCatalog().catch(() => null),
      ]);
      if (catalogRes?.statusCode === 1000 && catalogRes.data) setCatalog(catalogRes.data);
      if (plansRes.statusCode === 1000 && plansRes.data) setPlans(plansRes.data);
      else setLoadFailed(true);
      if (overviewRes.statusCode === 1000 && overviewRes.data) {
        setOverview(overviewRes.data);
        if (overviewRes.data.subscription?.billingCycle) setCycle(overviewRes.data.subscription.billingCycle);
      }
    } catch {
      setLoadFailed(true);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  // Only offer cycles some paid plan actually has a price for.
  const availableCycles = useMemo(
    () => CYCLES.filter((c) => plans.some((p) => !p.isFree && priceFor(p, c) > 0)),
    [plans]
  );
  useEffect(() => {
    if (availableCycles.length && !availableCycles.includes(cycle)) setCycle(availableCycles[0]);
  }, [availableCycles]);

  const savingsPercent = (plan: BillingPlan, c: BillingCycle) => {
    const monthly = plan.moPrice * CYCLE_MONTHS[c];
    const actual = priceFor(plan, c);
    return monthly > 0 && actual > 0 && actual < monthly ? Math.round((1 - actual / monthly) * 100) : 0;
  };

  const finishActivation = async (message: string) => {
    if (currentBusiness?._id) await activateBusinessSession(currentBusiness._id);
    showNotification('success', t('billing.planActivatedTitle'), message);
    navigateTo(isChangeMode ? 'subscription' : 'channel-onboarding-stepper');
  };

  const onChoose = async (plan: BillingPlan) => {
    if (!overview?.canManage) return;
    setBusyPlanId(plan._id);
    try {
      const startRes = await apiService.startPlan(plan._id);
      if (startRes.statusCode !== 1000 || !startRes.data) {
        showNotification('error', t('billing.errorTitle'), startRes.errors?.[0]?.message || t('billing.errorGeneric'));
        return;
      }
      if (startRes.data.activated) {
        await finishActivation(
          startRes.data.kind === 'trial'
            ? t('billing.trialStarted', { days: startRes.data.trialDays || plan.trialDays })
            : t('billing.freeStarted')
        );
        return;
      }
      // Requires payment.
      if (!canPayInApp()) {
        showNotification('info', t('billing.unavailableTitle'), t('billing.unavailableInApp'));
        return;
      }
      const checkout = await startCheckout(plan._id, cycle, locale === 'ar' ? 'ar' : 'en');
      if (checkout) {
        navigateTo('billing-return', undefined, { paymentId: checkout.paymentId, from: isChangeMode ? 'change' : 'onboarding' });
      }
    } catch (err) {
      const { title, message, type } = handleApiError(err);
      showNotification(type, title, message);
    } finally {
      setBusyPlanId(null);
    }
  };

  // Plan text in the app language when the plan has a translation.
  const textOf = (plan: BillingPlan) => {
    const tr = plan.translations?.[locale];
    return {
      name: tr?.name || plan.name,
      description: tr?.description || plan.description,
      features: tr?.features?.length ? tr.features : plan.features || [],
    };
  };

  // "What's included" from the plan's entitlements (limits first, then modules).
  const KEY_LIMITS = ['users', 'whatsapp_numbers', 'facebook_pages', 'x_accounts', 'phone_agents', 'ai_agents', 'products', 'contacts', 'initiated_chats'];
  const includedOf = (plan: BillingPlan) => {
    const ent = plan.entitlements;
    if (!ent || !catalog) return null;
    const lang = locale === 'ar' ? 'ar' : 'en';
    const limits = KEY_LIMITS
      .filter((k) => ent.limits?.[k] !== undefined && ent.limits[k] !== 0)
      .map((k) => {
        const f = catalog.features.find((x) => x.key === k && x.kind === 'limit');
        const v = ent.limits[k];
        return `${f?.label[lang] || k}: ${v === -1 ? t('billing.unlimited') : v.toLocaleString()}`;
      });
    const modules = catalog.features
      .filter((f) => f.kind === 'module' && ent.modules?.includes(f.key))
      .map((f) => `${f.label[lang]}${f.comingSoon ? ` (${t('billing.soon')})` : ''}`);
    return { limits, modules };
  };

  const ctaFor = (plan: BillingPlan): { label: string; enabled: boolean } => {
    // "Current plan" only while it is running on this cycle; in a trial,
    // grace or after it ended the same plan can be paid for.
    const isRunningCurrent = overview?.plan?._id === plan._id
      && (overview.state === 'active' || overview.state === 'free')
      && (plan.isFree || overview.subscription?.billingCycle === cycle);
    if (isRunningCurrent) return { label: t('billing.currentPlan'), enabled: false };
    if (plan.isFree) return { label: t('billing.startFree'), enabled: true };
    if (overview?.trialAvailable && plan.trialDays > 0) return { label: t('billing.startTrial', { days: plan.trialDays }), enabled: true };
    const price = priceFor(plan, cycle);
    if (!(price > 0)) return { label: t('billing.notOffered'), enabled: false };
    if (!canPayInApp()) return { label: t('billing.unavailableTitle'), enabled: false };
    return { label: t('billing.subscribeFor', { price: formatMoney(price, plan.currency, locale) }), enabled: true };
  };

  const textStart = isRTL ? 'right' : 'left';

  return (
    <DualPaneLayout>
      <SafeAreaView style={styles.container}>
        <StatusBar barStyle={themeMode === 'dark' ? 'light-content' : 'dark-content'} backgroundColor={colors.background.surface as string} />
        <ScrollView contentContainerStyle={styles.content} showsVerticalScrollIndicator={false}>
          {goBack && (
            <TouchableOpacity onPress={goBack} style={[styles.backButton, { flexDirection: isRTL ? 'row-reverse' : 'row' }]} hitSlop={8}>
              <DirectionalIcon name="chevron-back" size={20} color={colors.textDark as string} />
              <Text style={styles.backText}>{t('common.back')}</Text>
            </TouchableOpacity>
          )}
          <Text style={[styles.title, { fontFamily: typo.fontFamily.bold }]}>
            {isChangeMode ? t('billing.changePlanTitle') : t('billing.choosePlanTitle')}
          </Text>
          <Text style={styles.subtitle}>
            {overview?.trialAvailable ? t('billing.choosePlanSubtitleTrial') : t('billing.choosePlanSubtitle')}
          </Text>

          {availableCycles.length > 1 && (
            <View style={[styles.cycleSwitch, { flexDirection: isRTL ? 'row-reverse' : 'row' }]}>
              {availableCycles.map((c) => {
                const active = c === cycle;
                const bestSaving = Math.max(0, ...plans.filter((p) => !p.isFree).map((p) => savingsPercent(p, c)));
                return (
                  <TouchableOpacity key={c} style={[styles.cycleOption, active && styles.cycleOptionActive]} onPress={() => setCycle(c)}>
                    <Text style={[styles.cycleText, active && styles.cycleTextActive]}>{t(`billing.cycle.${c}` as any)}</Text>
                    {bestSaving > 0 && <Text style={styles.cycleSave}>{t('billing.save', { percent: bestSaving })}</Text>}
                  </TouchableOpacity>
                );
              })}
            </View>
          )}

          {overview && !overview.canManage && (
            <View style={styles.notice}>
              <Ionicons name="information-circle-outline" size={18} color={colors.textLight as string} />
              <Text style={[styles.noticeText, { textAlign: textStart }]}>{t('billing.ownerOnly')}</Text>
            </View>
          )}

          {isLoading ? (
            <ActivityIndicator size="large" color={colors.primary as string} style={styles.loader} />
          ) : loadFailed || plans.length === 0 ? (
            <View style={styles.emptyState}>
              <Ionicons name="cloud-offline-outline" size={36} color={colors.textLight as string} />
              <Text style={styles.emptyText}>{t('billing.plansLoadFailed')}</Text>
              <TouchableOpacity style={styles.retryButton} onPress={load}>
                <Text style={styles.retryText}>{t('common.retry')}</Text>
              </TouchableOpacity>
            </View>
          ) : (
            <View style={styles.cards}>
              {plans.map((plan) => {
                const price = priceFor(plan, cycle);
                const cta = ctaFor(plan);
                const disabled = !cta.enabled || !overview?.canManage || busyPlanId !== null;
                const saving = plan.isFree ? 0 : savingsPercent(plan, cycle);
                return (
                  <View key={plan._id} style={[styles.card, plan.isPopular && styles.cardPopular]}>
                    <View style={[styles.badgeRow, { flexDirection: isRTL ? 'row-reverse' : 'row' }]}>
                      {plan.isPopular && <Text style={[styles.badge, styles.badgePopular]}>{t('billing.popular')}</Text>}
                      {!plan.isFree && overview?.trialAvailable && plan.trialDays > 0 && (
                        <Text style={[styles.badge, styles.badgeTrial]}>{t('billing.trialBadge', { days: plan.trialDays })}</Text>
                      )}
                    </View>
                    <Text style={[styles.planName, { fontFamily: typo.fontFamily.bold, textAlign: textStart }]}>{textOf(plan).name}</Text>
                    <Text style={[styles.planPrice, { textAlign: textStart }]}>
                      {plan.isFree ? formatMoney(0, plan.currency, locale) : price > 0 ? formatMoney(price, plan.currency, locale) : '—'}
                      {(plan.isFree || price > 0) && <Text style={styles.planPer}> / {t(`billing.per.${plan.isFree ? 'monthly' : cycle}` as any)}</Text>}
                    </Text>
                    {saving > 0 && <Text style={[styles.saveLine, { textAlign: textStart }]}>{t('billing.saveVsMonthly', { percent: saving })}</Text>}
                    {!!textOf(plan).description && <Text style={[styles.planDescription, { textAlign: textStart }]}>{textOf(plan).description}</Text>}
                    {(() => {
                      const inc = includedOf(plan);
                      if (!inc) return null;
                      return (
                        <View style={styles.includedBox}>
                          <Text style={[styles.includedLimits, { textAlign: textStart }]}>{inc.limits.join(' · ')}</Text>
                          <Text style={[styles.includedModules, { textAlign: textStart }]}>{inc.modules.join(' · ')}</Text>
                        </View>
                      );
                    })()}
                    <View style={styles.featureList}>
                      {(plan.entitlements ? [] : textOf(plan).features.slice(0, 8)).map((feature, idx) => (
                        <View key={idx} style={[styles.featureRow, { flexDirection: isRTL ? 'row-reverse' : 'row' }]}>
                          <Ionicons name="checkmark-circle" size={16} color={colors.primary as string} />
                          <Text style={[styles.featureText, { textAlign: textStart }]}>{feature}</Text>
                        </View>
                      ))}
                    </View>
                    <TouchableOpacity
                      style={[styles.selectButton, disabled && styles.selectButtonDisabled]}
                      onPress={() => onChoose(plan)}
                      disabled={disabled}
                      activeOpacity={0.85}
                    >
                      {busyPlanId === plan._id ? (
                        <ActivityIndicator size="small" color={colors.literal.white as string} />
                      ) : (
                        <Text style={[styles.selectButtonText, disabled && styles.selectButtonTextDisabled]}>{cta.label}</Text>
                      )}
                    </TouchableOpacity>
                  </View>
                );
              })}
            </View>
          )}

          {!isChangeMode && overview?.state && overview.state !== 'none' && (
            <TouchableOpacity onPress={() => navigateTo('channel-onboarding-stepper')} style={styles.skipLink}>
              <Text style={styles.skipText}>{t('billing.keepCurrent')}</Text>
            </TouchableOpacity>
          )}
          {canPayInApp() && <Text style={styles.securityNote}>{t('billing.securityNote')}</Text>}
        </ScrollView>
      </SafeAreaView>
    </DualPaneLayout>
  );
};

const getStyles = () => StyleSheet.create({
  container: { flex: 1, backgroundColor: colors.background.surface },
  content: { flexGrow: 1, paddingHorizontal: spacing.screenPadding, paddingTop: spacing.xl, paddingBottom: spacing.xl },
  backButton: { alignItems: 'center', gap: 4, alignSelf: 'flex-start', marginBottom: spacing.md },
  backText: { fontSize: typography.fontSize.sm, color: colors.textDark, fontFamily: typography.fontFamily.medium },
  title: { fontSize: typography.fontSize['2xl'], color: colors.textDark, textAlign: 'center', marginBottom: spacing.sm },
  subtitle: { fontSize: typography.fontSize.sm, color: colors.textLight, textAlign: 'center', marginBottom: spacing.lg },
  loader: { marginTop: spacing.xl * 2 },
  cycleSwitch: {
    alignSelf: 'center',
    backgroundColor: colors.background.secondary,
    borderRadius: 12,
    padding: 4,
    gap: 4,
    marginBottom: spacing.lg,
  },
  cycleOption: { paddingVertical: 8, paddingHorizontal: spacing.md, borderRadius: 9, alignItems: 'center' },
  cycleOptionActive: { backgroundColor: colors.background.surface },
  cycleText: { fontSize: typography.fontSize.sm, color: colors.textLight, fontFamily: typography.fontFamily.medium },
  cycleTextActive: { color: colors.textDark, fontFamily: typography.fontFamily.semibold },
  cycleSave: { fontSize: 10, color: colors.status.success, fontFamily: typography.fontFamily.semibold, marginTop: 1 },
  notice: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs,
    padding: spacing.sm,
    borderRadius: 10,
    backgroundColor: colors.background.secondary,
    marginBottom: spacing.md,
  },
  noticeText: { flex: 1, fontSize: typography.fontSize.sm, color: colors.textLight },
  cards: { gap: spacing.md },
  card: {
    padding: spacing.lg,
    borderRadius: spacing.borderRadius.lg,
    borderWidth: 1,
    borderColor: colors.border,
    backgroundColor: colors.background.surfaceRaised,
  },
  cardPopular: { borderColor: colors.primary, borderWidth: 2 },
  badgeRow: { gap: spacing.xs, flexWrap: 'wrap', marginBottom: spacing.xs },
  badge: {
    fontSize: typography.fontSize.xs,
    fontFamily: typography.fontFamily.semibold,
    paddingHorizontal: spacing.sm,
    paddingVertical: 2,
    borderRadius: 8,
    overflow: 'hidden',
  },
  badgePopular: { backgroundColor: colors.primary, color: colors.literal.white },
  badgeTrial: { backgroundColor: withAlpha(colors.status.success, 0.14), color: colors.status.success },
  planName: { fontSize: typography.fontSize.lg, color: colors.textDark },
  planPrice: { fontSize: typography.fontSize.xl, color: colors.primary, marginTop: spacing.xs, fontFamily: typography.fontFamily.bold },
  planPer: { fontSize: typography.fontSize.sm, color: colors.textLight, fontFamily: typography.fontFamily.regular },
  saveLine: { fontSize: typography.fontSize.xs, color: colors.status.success, marginTop: 2, fontFamily: typography.fontFamily.semibold },
  planDescription: { fontSize: typography.fontSize.sm, color: colors.textLight, marginTop: spacing.xs },
  featureList: { marginTop: spacing.md, gap: spacing.xs },
  includedBox: { marginTop: spacing.md, gap: 6 },
  includedLimits: { fontSize: typography.fontSize.sm, color: colors.textDark, fontFamily: typography.fontFamily.semibold, lineHeight: 20 },
  includedModules: { fontSize: typography.fontSize.xs, color: colors.textLight, lineHeight: 18 },
  featureRow: { alignItems: 'center', gap: spacing.xs },
  featureText: { flex: 1, fontSize: typography.fontSize.sm, color: colors.textDark },
  selectButton: {
    marginTop: spacing.lg,
    backgroundColor: colors.primary,
    borderRadius: spacing.borderRadius.lg,
    paddingVertical: spacing.sm,
    alignItems: 'center',
    minHeight: 44,
    justifyContent: 'center',
  },
  selectButtonDisabled: { backgroundColor: colors.background.secondary },
  selectButtonText: { color: colors.literal.white, fontSize: typography.fontSize.base, fontFamily: typography.fontFamily.semibold },
  selectButtonTextDisabled: { color: colors.textLight },
  emptyState: { alignItems: 'center', gap: spacing.sm, marginTop: spacing.xl },
  emptyText: { fontSize: typography.fontSize.sm, color: colors.textLight, textAlign: 'center' },
  retryButton: { paddingHorizontal: spacing.lg, paddingVertical: spacing.sm, borderRadius: 10, borderWidth: 1, borderColor: colors.primary },
  retryText: { color: colors.primary, fontFamily: typography.fontFamily.semibold },
  skipLink: { alignSelf: 'center', marginTop: spacing.lg, padding: spacing.sm },
  skipText: { color: colors.textLight, fontSize: typography.fontSize.sm, textDecorationLine: 'underline' },
  securityNote: { fontSize: typography.fontSize.xs, color: colors.textLight, textAlign: 'center', marginTop: spacing.lg },
});
